import { Button } from '@/app/common/components/Button'
import { AppRoutePaths } from '@/routing/route_paths'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { AuthLayout } from '../layout'
import { z } from 'zod'
import { useEffect, useState } from 'react'
import { useAppAlert } from '@/app/AppAlertProvider'
import { VerifyEmail } from '../../../../apis/users/requests'
import { CircleLoader } from '@/app/common/components/CircleLoader'
import { useTranslation } from 'react-i18next'
import AceScholarshipsLogo  from '@assets/AceScholarshipLogo1.jpg'

export default function EmailVerification() {
  const { t: translate } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { fireAlert } = useAppAlert()
  const [emailVerified, setEmailVerified] = useState(false)
  const [verificationParams, setVerificationParams] =
    useState<null | QueryInvitation>(null)

  useEffect(() => {
    decodeBase64Verification(searchParams.get('q') ?? '')
      .then((qVerification) => {
        setVerificationParams(qVerification)
      })
      .catch((reason) => {
        console.error(reason)
        fireAlert({
          variant: 'error',
          message: 'Invalid request',
        }).then(() => {
          navigate(AppRoutePaths.auth.login)
        })
      })
  }, [])

  useEffect(() => {
    if (verificationParams?.user_id && verificationParams?.token) {
      doVerify()
    }
  }, [verificationParams])

  const doVerify = async () => {
    const { error } = await VerifyEmail(
      verificationParams!.user_id,
      verificationParams!.token
    )
    if (error) {
      await fireAlert({
        variant: 'error',
        message: error.message,
      })
      navigate(AppRoutePaths.auth.login)
    } else {
      setEmailVerified(true)
    }
  }

  const handleSubmit = () => {
    navigate(AppRoutePaths.auth.login)
  }

  return (
    <AuthLayout>
      <AuthLayout.Content className="space-y-5">
        {emailVerified ? (
          <div className="flex flex-col">
            <img
              alt={translate('ace_logo')}
              src={AceScholarshipsLogo}
              className="mx-auto h-auto"
              width="100%"
            />
            <h3 className="text-lg font-semibold text-gray-900 text-center mt-8 mb-4">
              {translate('your_email_has_been_verified')}
            </h3>
            <div className="text-sm text-gray-500 font-medium text-center mb-7">
              {translate('you_can_now_sign_in_to_your_scholarship')}
              <br />
              {translate('account')}
            </div>
            <div className="flex justify-center">
              <Button onClick={handleSubmit}>{translate('sign_in')}</Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-center gap-3">
            <CircleLoader size="sm" />
            <span>{translate('processing')}...</span>
          </div>
        )}
      </AuthLayout.Content>
    </AuthLayout>
  )
}

const querySchema = z.object({
  user_id: z.string(),
  email: z.string().email(),
  token: z.string(),
})
type QueryInvitation = z.infer<typeof querySchema>

async function decodeBase64Verification(
  base64Str: string
): Promise<QueryInvitation> {
  const decodedUrlQueryString = atob(base64Str)

  // Create a URLSearchParams object to parse the query string
  const params = new URLSearchParams(decodedUrlQueryString)

  // Convert URLSearchParams to an object
  const queryObj: { [key: string]: string } = {}
  params.forEach((value, key) => {
    queryObj[key] = value
  })

  // Validate the query object against the schema
  return querySchema.parse(queryObj)
}
